import { memo, useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import styled from '@mint-lib/styled';

import {
  Box,
  Button,
  Dialog,
  Icon,
  IconButton,
  LoadingButton,
  Select,
  Tooltip,
  Typography,
  ValueTypes,
} from '@myn/mui';
import { rem } from '@myn/shared/src/utils';

import {
  useCreatePredictiveMediaPlanVersionMutation,
  useUpdateMediaPlanRowsBudgetMutation,
} from '../../../api/mediaPlansApiSlice.js';
import { BUILDER_ROW_TYPES } from '../../../constants/campaignBuilder.js';
import {
  usePredictiveBudget,
  useSetBudgetRows,
} from '../../../hooks/useBudgetRows.js';
import { useCampaignCurrencyMode } from '../../../hooks/useCampaignCurrencyMode.js';
import {
  resetPredictivePlanningQueryData,
  useGetPredictivePlanningData,
  usePredictiveDataSelector,
} from '../../../hooks/useGetPredictivePlanningData.js';
import {
  usePredictivePlanningMetricList,
  useSelectedKPI,
} from '../../../hooks/usePredictivePlanningMetrics.js';
import { useSetPredictivePlanningMode } from '../../../hooks/usePredictivePlanningMode.js';
import PredictiveAcceptModal from '../../../molecules/PredictiveModals/PredictiveAcceptModal.jsx';
import {
  PredictiveData,
  type PredictiveDataType,
} from '../../../redux/reducer/campaigns/mediaPlans/types.js';
import { Currency } from '../../../types/_campaigns/campaigns.js';
import type { BuyingPlanInfo } from '../../../types/buyingPlanInfo.js';
import BudgetField from './BudgetField.jsx';
import CampaignCurrencySwitch from './CampaignCurrencySwitch.jsx';
import useViewportWidth from './useViewportWidth.jsx';

type Props = {
  selectedPlan: BuyingPlanInfo;
  isAllRowsLocked: boolean;
  currency?: Currency;
};

const prepareOptimalBudgets = (
  predictiveData: PredictiveData,
  isCampaignCurrency: boolean,
) => {
  const currentBudget: keyof PredictiveDataType = isCampaignCurrency
    ? 'optimalBudget'
    : 'optimalBudgetInLocalCurrency';

  const isTileUnlockedWithBudget = (id: string) => {
    const { isLocked, type, [currentBudget]: budget } = predictiveData[id];
    const isMediaRowLevel = type === BUILDER_ROW_TYPES.TILE;

    return isMediaRowLevel && !isLocked && budget !== undefined;
  };

  return Object.entries(predictiveData)
    .filter(([id, _]) => isTileUnlockedWithBudget(id))
    .map(([id, data]) => ({
      id,
      budget: data[currentBudget],
    }));
};

const PredictiveSettings: React.FC<Props> = memo(
  ({ selectedPlan, currency, isAllRowsLocked }) => {
    const { t } = useTranslation('@myn/planning');

    const { data: predictiveData } = usePredictiveDataSelector('mediaRows');
    const [selectedKPI, setSelectedKPI] = useSelectedKPI();
    const [isCampaignCurrencyMode] = useCampaignCurrencyMode();
    const setPredictivePlanningMode = useSetPredictivePlanningMode();
    const setBudgetRows = useSetBudgetRows();
    const { isTabletWidth } = useViewportWidth();

    const kpiOptions = usePredictivePlanningMetricList();
    const [createPredictiveMediaPlanVersion] =
      useCreatePredictiveMediaPlanVersionMutation();

    const {
      isSimulationLoading,
      isSimulationSuccess,
      fetchPredictivePlanningData,
    } = useGetPredictivePlanningData();

    const [updateMediaPlanRowsBudget] = useUpdateMediaPlanRowsBudgetMutation();
    const [budget, setBudget] = usePredictiveBudget();
    const [isBudgetModalOpen, setIsBudgetModalOpen] = useState<boolean>(false);
    const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);

    const [isUpdateWithAllocatedBudget, setIsUpdateWithAllocatedBudget] =
      useState(false);

    const preparedOptimalBudget = useMemo(() => {
      if (!predictiveData) {
        return [];
      }

      return prepareOptimalBudgets(
        predictiveData as PredictiveData,
        isCampaignCurrencyMode,
      );
    }, [predictiveData, isCampaignCurrencyMode]);

    const isSimulationDisabled =
      !selectedKPI || isSimulationLoading || isAllRowsLocked || !Number(budget);

    const tooltipTitle = useMemo(
      () =>
        isSimulationDisabled &&
        !isSimulationLoading &&
        t('Add a budget bigger than 0 and select KPI to run simulation'),

      [isSimulationDisabled],
    );

    const resetPredictivePlanningData = () => {
      setPredictivePlanningMode(false);
      setBudgetRows(null);
      setSelectedKPI(null);
      resetPredictivePlanningQueryData();
    };

    const applyBudget = async () => {
      setIsUpdateWithAllocatedBudget(false);
      await updateMediaPlanRowsBudget({
        id: selectedPlan.id,
        optimalBudgets: preparedOptimalBudget,
      });
      resetPredictivePlanningData();
    };

    const onSimulationError = () => {
      enqueueSnackbar({
        variant: 'error',
        title: t('Predictive Planning simulation failed'),
        subTitle: t(
          'The simulation failed, please try again.\nIf the issue keeps happening, please contact Support.',
        ),
        autoHideDuration: 3000,
      });
    };

    const createNewPredictiveMediaPlanVersion = async () => {
      setIsUpdateWithAllocatedBudget(false);
      await createPredictiveMediaPlanVersion({
        id: selectedPlan.id,
        optimalBudgets: preparedOptimalBudget,
      });
      resetPredictivePlanningData();
    };

    const handleRunSimulation = async () => {
      try {
        await fetchPredictivePlanningData({ throwOnError: true });
      } catch (error) {
        onSimulationError();
      }
    };

    const handleSave = () => {
      const newBudget = preparedOptimalBudget.reduce(
        (acc, row) => acc + Number(row.budget),
        0,
      );

      return newBudget >
        Number(selectedPlan.budget) +
          Number(selectedPlan.campaignUnallocatedBudget)
        ? setIsBudgetModalOpen(true)
        : setIsVersionModalOpen(true);
    };

    const handleChangeKPI = (option: ValueTypes) => {
      if (!Array.isArray(option)) {
        setSelectedKPI(String(option));
      }
    };

    const handleChangeBudget = (value: number | null) => {
      setBudget(value);
    };

    const handleCancel = () => {
      resetPredictivePlanningData();
      setIsUpdateWithAllocatedBudget(false);
    };

    const handleCloseModal = useCallback(() => {
      setIsBudgetModalOpen(false);
    }, []);

    const handleCloseVersionModal = useCallback(() => {
      setIsVersionModalOpen(false);
    }, []);

    const handleAcceptBudget = () => {
      setIsBudgetModalOpen(false);
      setIsVersionModalOpen(true);
      setIsUpdateWithAllocatedBudget(true);
    };

    const trackEvent = useCallback(() => {
      setIsUpdateWithAllocatedBudget(false);
    }, []);

    const handleUpdateCurrentVersion = useCallback(() => {
      applyBudget();
      setIsVersionModalOpen(false);
      if (isUpdateWithAllocatedBudget) {
        trackEvent();
      }
    }, [isUpdateWithAllocatedBudget, applyBudget]);

    const handleCreateNewVersion = useCallback(() => {
      createNewPredictiveMediaPlanVersion();
      setIsVersionModalOpen(false);
      if (isUpdateWithAllocatedBudget) {
        trackEvent();
      }
    }, [isUpdateWithAllocatedBudget, createNewPredictiveMediaPlanVersion]);

    return (
      <Wrapper>
        <FieldsWrapper>
          <IconButton
            variant="icon"
            color="primary"
            size="medium"
            icon="ArrowLeft"
            onClick={handleCancel}
            sx={{ alignSelf: 'flex-end', m: '2px' }}
          />
          <BudgetFieldWrapper>
            <BudgetField
              currency={currency}
              value={budget}
              onChange={handleChangeBudget}
            />
          </BudgetFieldWrapper>
          <SelectInput
            label={t('KPI to be maximized*')}
            placeholder={selectedKPI || t('Select KPI')}
            options={kpiOptions}
            onChange={handleChangeKPI}
            disabled={!kpiOptions.length || isSimulationLoading}
            size="small"
            value={selectedKPI || ''}
          />
          <CampaignCurrencySwitch
            size={isTabletWidth ? 'small' : 'large'}
            isDisabled={isSimulationLoading || !isSimulationSuccess}
          />
        </FieldsWrapper>

        <ButtonsContainer>
          <Tooltip title={tooltipTitle} placement="bottom">
            <span>
              <RunSimulationButton
                type="button"
                variant="contained"
                color="secondary"
                onClick={handleRunSimulation}
                disabled={isSimulationDisabled || !kpiOptions.length}
                size="medium"
                loading={isSimulationLoading}
                loadingPosition={'start'}
                startIcon={!isSimulationLoading && <Icon component="Idea" />}
              >
                <Typography variant="bodyShort01">
                  {t('Run Simulation')}
                </Typography>
              </RunSimulationButton>
            </span>
          </Tooltip>
          <AcceptButton
            color="primary"
            variant="contained"
            disabled={
              !predictiveData || isSimulationLoading || !isSimulationSuccess
            }
            onClick={handleSave}
          >
            <Typography variant="bodyShort01">{t('Accept')}</Typography>
          </AcceptButton>
          <Button onClick={handleCancel} variant="text" color="secondary">
            <Typography variant="bodyShort01">{t('Cancel')}</Typography>
          </Button>
        </ButtonsContainer>

        <Dialog
          open={isBudgetModalOpen}
          onClose={handleCloseModal}
          cancelButtonProps={{
            label: t('Cancel'),
            onClick: handleCloseModal,
          }}
          submitButtonProps={{
            label: t('ACCEPT'),
            onClick: handleAcceptBudget,
          }}
          title={t('Are you sure you want to accept new budget?')}
        >
          <ModalText>
            {`${t(
              'The Recommendation budget exceeds the Brief budget. Are you sure you',
            )}
         ${t('want to update the Brief budget?')}`}
          </ModalText>
        </Dialog>

        <PredictiveAcceptModal
          isVersionModalOpen={isVersionModalOpen}
          handleCloseVersionModal={handleCloseVersionModal}
          handleCreateNewVersion={handleCreateNewVersion}
          handleUpdateCurrentVersion={handleUpdateCurrentVersion}
        />
      </Wrapper>
    );
  },
);

const AcceptButton = styled(Button)`
  :not([disabled]) {
    background-color: ${({ theme }) => theme.palette.primary.main} !important;
  }
`;

const ButtonsContainer = styled('div')`
  display: flex;
  align-items: center;
  position: relative;
  bottom: 2px;

  button {
    margin-right: ${({ theme }) => theme.spacing(2)};
  }
`;

const RunSimulationButton = styled(LoadingButton)`
  @media screen and (max-width: 1028px) {
    width: 150px;
  }
`;

const BudgetFieldWrapper = styled(Box)`
  width: 200px;

  input {
    text-align: right;
  }

  @media screen and (max-width: 1028px) {
    width: 120px;
  }
`;

const SelectInput = styled(Select)`
  text-transform: capitalize;

  @media screen and (max-width: 1360px) {
    width: 190px;
  }

  @media screen and (max-width: 1028px) {
    width: 140px;
  }
`;

const Wrapper = styled('div')`
  border-radius: 5px;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const FieldsWrapper = styled('div')`
  display: flex;
  align-items: end;
  gap: ${({ theme }) => theme.spacing(2)};

  @media screen and (max-width: 1280px) {
    gap: ${({ theme }) => theme.spacing(1)};
  }
`;

const ModalText = styled('div')`
  font-weight: 500;
  font-size: ${rem(16)};
  line-height: ${rem(22)};
  color: ${'#656565'};
`;

export default PredictiveSettings;
